import type { LabelPrintBatch, LabelPrintBatchDetail, PurchaseOrder } from "@shared/types";

export interface DemoBatchesOptions {
  /** Orders to list inside the selected batch's detail (the roaster's demo orders) */
  orders: readonly PurchaseOrder[];
  /** Anchor for the relative batch dates; injectable for tests */
  now?: Date;
}

export interface DemoBatches {
  batches: LabelPrintBatch[];
  /** Detail of the first batch; its id matches a row in `batches` so the summary anchors */
  batch: LabelPrintBatchDetail;
}

const DAY_MS = 86_400_000;

const buildBatch = (
  batchId: string,
  overrides: Partial<LabelPrintBatch> & Pick<LabelPrintBatch, "batchStatus" | "orderTotal" | "createdAt">
): LabelPrintBatch => ({
  type: "labelPrintBatch",
  vendorKey: "demo-vendor",
  batchId,
  batchPrintStatus: "LabelsOnly",
  totalWeightInGrams: overrides.orderTotal * 500,
  totalUnits: overrides.orderTotal * 2,
  originalOrderCount: overrides.orderTotal,
  totalOrders: overrides.orderTotal,
  labelCreatedOrders: overrides.orderTotal,
  failedForRetryOrders: 0,
  totalLabelPrintAttempts: 1,
  ...overrides,
});

/** Sample batches plus the detail of the first, which the summary, metrics and print-action steps spotlight */
export const buildDemoBatches = ({ orders, now = new Date() }: DemoBatchesOptions): DemoBatches => {
  const daysAgo = (days: number): string => new Date(now.getTime() - days * DAY_MS).toISOString();
  const batches = [
    buildBatch("DEMO-BATCH-003", { batchStatus: "Completed", orderTotal: 4, createdAt: daysAgo(1), batchNotes: "Morning dispatch" }),
    buildBatch("DEMO-BATCH-002", { batchStatus: "Completed", orderTotal: 3, createdAt: daysAgo(3), batchNotes: "Weekend orders" }),
    buildBatch("DEMO-BATCH-001", {
      batchStatus: "Processing",
      batchPrintStatus: "Processing",
      orderTotal: 2,
      labelCreatedOrders: 1,
      createdAt: daysAgo(6),
    }),
  ];
  const [first] = batches;
  if (!first) throw new Error("demo batches must not be empty");
  return { batches, batch: { ...first, purchaseOrders: orders.slice(0, first.orderTotal) } };
};
